import React, { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import { useCachedData } from '../hooks/useCachedData';
import { useQuery } from '@tanstack/react-query';
import { endpoints, api } from '../api/config';
import {
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLUMN_MIN_SIZE, COLUMN_SIZE, DIALOG_CLOSE_BUTTON_STYLE, RESPONSIVE_DIALOG_PAPER_PROPS } from '../constants/styles';

const InsufficientInventory = () => {
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [selectedShipVia, setSelectedShipVia] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedPoDetails, setSelectedPoDetails] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('insufficient-inventory-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [detailsColumnVisibility, setDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('insufficient-inventory-details-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Fetch locations data
  const { data: locationsData, isLoading: locationsLoading } = useCachedData(endpoints.locations);

  // Transform locations data for dropdown
  const locationOptions = useMemo(() => {
    if (!locationsData) return [];
    
    // Handle both array and object formats
    if (Array.isArray(locationsData)) {
      return locationsData.map(loc => ({
        value: loc.location || loc.name || loc.id,
        label: loc.location || loc.name || loc.id
      }));
    }
    
    return Object.values(locationsData).map(loc => ({
      value: loc.location || loc.name || loc.id,
      label: loc.location || loc.name || loc.id
    }));
  }, [locationsData]);

  // Set default location to L60 when locations are loaded
  useEffect(() => {
    if (locationOptions.length > 0 && !selectedLocation) {
      // Try to find L60 location - check for "L60" or "L60-Hardeeville_SC"
      const l60Location = locationOptions.find(opt => 
        opt.value === 'L60-Hardeeville_SC' || 
        opt.value === 'L60' || 
        opt.value?.includes('L60') ||
        opt.label === 'L60-Hardeeville_SC' ||
        opt.label === 'L60' ||
        opt.label?.includes('L60')
      );
      
      if (l60Location) {
        setSelectedLocation(l60Location.value);
      }
    }
  }, [locationOptions, selectedLocation]);

  // Fetch insufficient inventory data using useQuery with caching
  const { data: insufficientInventoryRaw, isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: ['wms_data', 'ltlInsufficientInventory', selectedLocation],
    queryFn: async () => {
      if (!selectedLocation) return null;
      try {
        console.log(`🔄 Fetching insufficient inventory for location ${selectedLocation}...`);
        const response = await api.get(endpoints.ltlInsufficientInventory, {
          params: {
            location: selectedLocation
          }
        });
        console.log(`✅ Successfully fetched insufficient inventory:`, response.data);
        // Handle response data - API returns { message: "...", items: [...] }
        const data = response.data?.items || response.data?.data || response.data;
        return data;
      } catch (error) {
        console.error(`❌ Error fetching insufficient inventory:`, error);
        throw error;
      }
    },
    enabled: !!selectedLocation,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    retry: 1,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  });

  // Extract unique carrier_name and ship_via values from API response
  const { carrierOptions, shipViaOptions } = useMemo(() => {
    if (!insufficientInventoryRaw) return { carrierOptions: [], shipViaOptions: [] };
    
    let rawData = [];
    if (Array.isArray(insufficientInventoryRaw)) {
      rawData = insufficientInventoryRaw;
    } else if (insufficientInventoryRaw && typeof insufficientInventoryRaw === 'object') {
      if (insufficientInventoryRaw.items && Array.isArray(insufficientInventoryRaw.items)) {
        rawData = insufficientInventoryRaw.items;
      } else {
        rawData = Object.values(insufficientInventoryRaw);
      }
    }
    
    const carrierSet = new Set();
    const shipViaSet = new Set();
    
    rawData.forEach(item => {
      // Check at item level
      if (item.carrier_name) carrierSet.add(item.carrier_name);
      if (item.ship_via) shipViaSet.add(item.ship_via);
      
      // Check in po_details
      if (Array.isArray(item.po_details)) {
        item.po_details.forEach(poDetail => {
          if (poDetail.carrier_name) carrierSet.add(poDetail.carrier_name);
          if (poDetail.ship_via) shipViaSet.add(poDetail.ship_via);
        });
      }
    });
    
    const carrierOptions = Array.from(carrierSet)
      .filter(val => val != null && val !== '')
      .sort()
      .map(val => ({ value: val, label: val }));
    
    const shipViaOptions = Array.from(shipViaSet)
      .filter(val => val != null && val !== '')
      .sort()
      .map(val => ({ value: val, label: val }));
    
    return { carrierOptions, shipViaOptions };
  }, [insufficientInventoryRaw]);

  // Process and flatten the inventory data with filtering
  const inventoryData = useMemo(() => {
    if (!insufficientInventoryRaw) return [];
    
    let rawData = [];
    if (Array.isArray(insufficientInventoryRaw)) {
      rawData = insufficientInventoryRaw;
    } else if (insufficientInventoryRaw && typeof insufficientInventoryRaw === 'object') {
      // If it's an object, check if it has an items property
      if (insufficientInventoryRaw.items && Array.isArray(insufficientInventoryRaw.items)) {
        rawData = insufficientInventoryRaw.items;
      } else {
        // If it's an object, convert to array
        rawData = Object.values(insufficientInventoryRaw);
      }
    }
    
    // Flatten the data: each item becomes a row
    const firstPoDetail = (poDetails) => {
      const arr = Array.isArray(poDetails) ? poDetails : [];
      return arr[0] || null;
    };
    let mappedData = rawData.map(item => {
      const po = firstPoDetail(item.po_details);
      return {
        item: item.item || '-',
        po_number: item.po_number ?? po?.po_number ?? null,
        missing_qty: item.missing_qty || 0,
        po_details: item.po_details || [],
        carrier_name: item.carrier_name ?? po?.carrier_name ?? null,
        ship_via: item.ship_via ?? po?.ship_via ?? null
      };
    });
    
    // Apply filters
    if (selectedCarrier) {
      mappedData = mappedData.filter(item => {
        // Check item level
        if (item.carrier_name === selectedCarrier) return true;
        // Check po_details
        if (Array.isArray(item.po_details)) {
          return item.po_details.some(poDetail => poDetail.carrier_name === selectedCarrier);
        }
        return false;
      });
    }
    
    if (selectedShipVia) {
      mappedData = mappedData.filter(item => {
        // Check item level
        if (item.ship_via === selectedShipVia) return true;
        // Check po_details
        if (Array.isArray(item.po_details)) {
          return item.po_details.some(poDetail => poDetail.ship_via === selectedShipVia);
        }
        return false;
      });
    }
    
    return mappedData;
  }, [insufficientInventoryRaw, selectedCarrier, selectedShipVia]);

  // Extract error message
  const error = queryError ? (queryError.response?.data?.message || queryError.message || 'Failed to load insufficient inventory data') : null;

  // One-time: clear saved column order if it still has old "available_qty" so PO Number appears second
  const clearedColumnOrderRef = useRef(false);
  if (!clearedColumnOrderRef.current && typeof window !== 'undefined') {
    clearedColumnOrderRef.current = true;
    try {
      const key = 'Insufficient Inventory-column-order';
      const saved = localStorage.getItem(key);
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.includes('available_qty')) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('insufficient-inventory-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Save details column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('insufficient-inventory-details-column-visibility', JSON.stringify(detailsColumnVisibility));
  }, [detailsColumnVisibility]);

  // Handle row click to open PO details dialog
  const handleRowClick = (row) => {
    const poDetails = row.po_details;
    if (poDetails && Array.isArray(poDetails) && poDetails.length > 0) {
      setSelectedPoDetails(poDetails);
      setSelectedItem(row.item);
      setDetailsOpen(true);
    }
  };

  // Main table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'item',
      header: 'Item',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value || '-';
      }
    },
    {
      accessorKey: 'po_number',
      header: 'PO Number',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value ?? '-';
      }
    },
    {
      accessorKey: 'missing_qty',
      header: 'Missing Qty',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return typeof value === 'number' ? value : (value || 0);
      }
    },
    {
      accessorKey: 'carrier_name',
      header: 'Carrier',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value ?? '-';
      }
    },
    {
      accessorKey: 'ship_via',
      header: 'Ship',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value ?? '-';
      }
    }
  ], []);

  // PO Details table columns
  const poDetailsColumns = useMemo(() => [
    {
      accessorKey: 'po_number',
      header: 'PO Number',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return value || '-';
      }
    },
    {
      accessorKey: 'qty',
      header: 'Quantity',
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: info => {
        const value = info.getValue();
        return typeof value === 'number' ? value : (value || 0);
      }
    }
  ], []);

  const insufficientInventoryMainGrid = useDataGridProTable({
    data: inventoryData,
    loading,
    error,
    title: 'Insufficient Inventory',
    columns,
    onDataRefresh: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleRowClick,
  });

  const insufficientInventoryPoDetailsGrid = useDataGridProTable({
    data: selectedPoDetails || [],
    columns: poDetailsColumns,
    title: 'insufficient-po-details',
    initialColumnVisibility: detailsColumnVisibility,
    onColumnVisibilityChange: setDetailsColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
    loading: false,
    error: null,
  });

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5 }}>
        {/* Header with Filters */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.25rem' }}>
              Insufficient Inventory
            </Typography>
          </Box>
          
          {/* Filter Dropdowns */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Location Dropdown */}
            <Box sx={{ maxWidth: 300, minWidth: 200 }}>
              <TextField
                select
                fullWidth
                label="Location"
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  // Reset carrier and ship_via when location changes
                  setSelectedCarrier('');
                  setSelectedShipVia('');
                }}
                size="small"
                disabled={locationsLoading}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                    height: '36px'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                  Select a location
                </MenuItem>
                {locationOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Carrier Name Dropdown */}
            {selectedLocation && carrierOptions.length > 0 && (
              <Box sx={{ maxWidth: 300, minWidth: 200 }}>
                <TextField
                  select
                  fullWidth
                  label="Carrier Name"
                  value={selectedCarrier}
                  onChange={(e) => setSelectedCarrier(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      height: '36px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                    All Carriers
                  </MenuItem>
                  {carrierOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}

            {/* Ship Via Dropdown */}
            {selectedLocation && shipViaOptions.length > 0 && (
              <Box sx={{ maxWidth: 300, minWidth: 200 }}>
                <TextField
                  select
                  fullWidth
                  label="Ship Via"
                  value={selectedShipVia}
                  onChange={(e) => setSelectedShipVia(e.target.value)}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '0.875rem',
                      height: '36px'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>
                    All Ship Via
                  </MenuItem>
                  {shipViaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
          </Box>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {/* Error message */}
        {error && !loading && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {/* Data Table */}
        {!loading && selectedLocation && (
          <MuiXDataGridSurface hook={insufficientInventoryMainGrid} DataGridPro={DataGridPro} />
        )}

        {/* Empty state */}
        {!loading && !error && !selectedLocation && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Please select a location to view insufficient inventory data
            </Typography>
          </Box>
        )}

        {/* No data state */}
        {!loading && !error && selectedLocation && inventoryData.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No insufficient inventory data found for the selected location
            </Typography>
          </Box>
        )}
      </Box>

      {/* PO Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedPoDetails(null);
          setSelectedItem(null);
        }}
        maxWidth={false}
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: RESPONSIVE_DIALOG_PAPER_PROPS }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          PO Details{selectedItem ? ` - ${selectedItem}` : ''}
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => {
              setDetailsOpen(false);
              setSelectedPoDetails(null);
              setSelectedItem(null);
            }}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 0, overflow: 'hidden' }}>
          {selectedPoDetails && selectedPoDetails.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={insufficientInventoryPoDetailsGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No PO details available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default InsufficientInventory;

